import { Component, Input, OnChanges, OnInit, Optional, Output, Self, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { map, startWith, Subject } from 'rxjs';

export const NOOP_VALUE_ACCESSOR: ControlValueAccessor = {
  writeValue(): void {},
  registerOnChange(): void {},
  registerOnTouched(): void {},
};

@Component({
  selector: 'app-multi-select-with-search',
  templateUrl: './multi-select-with-search.component.html',
  styleUrl: './multi-select-with-search.component.scss'
})
export class MultiSelectWithSearchComponent<TData> implements OnInit, OnChanges {
  @Input({required: true}) label!: string;
  @Input({required: true}) itemsToSelect: TData[] = [];
  @Input({required: true}) selectAllValue!: TData;
  @Input() multiple: boolean = false;
  @Input() panelWidth = "auto";
  @Input() compareWith: (item1: TData, item2: TData) => boolean = ((item1: TData, item2: TData) => item1 === item2);
  @Input() getOptionName: (item: TData) => string = ((item: TData) => item as string);
  @Input() getOptionNameTrigger!: (item: TData) => string;

  @Output() valueChanges: Subject<TData[] | TData> = new Subject<TData[] | TData>();

  public filteredItems: TData[] = [];
  public searchFormControl: FormControl<string|null> = new FormControl<string>('');

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = NOOP_VALUE_ACCESSOR;
    }
  }

  ngOnInit(): void {
    this.getOptionNameTrigger = this.getOptionNameTrigger ?? this.getOptionName;
    this.filteredItems = this.itemsToSelect.slice();
    this.setSelectAll();
    this.itemFormControl.valueChanges.subscribe(newValues => {
      this.setSelectAll();
    });
    this.searchFormControl.valueChanges
      .pipe(
        startWith(null),
        map((searchValue: string | null) => (searchValue ? this._filterItems(searchValue) : this.itemsToSelect.slice()))
      )
      .subscribe((filtered) => {
        this.filteredItems = filtered;
        this.setSelectAll();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemsToSelect?.currentValue) {
      this.itemsToSelect = [...changes.itemsToSelect.currentValue];
      this.searchFormControl.setValue('', {emitEvent: true});
    }
  }

  public onSelectAllClicked() {
    if (!this.multiple) {
      return;
    }
    const itemFormControlValue = this.itemFormControlValue as TData[];
    const isAllSelected = itemFormControlValue.find(_ => !this.isNotSelectAllValue(_));
    if (isAllSelected) {
      this.itemFormControl.setValue([], {emitEvent: false, onlySelf: true});
    } else {
      const values = itemFormControlValue.filter(_ => this.isNotSelectAllValue(_));
      const newValuesUnordered = values.concat(this.filteredItems);
      this.itemFormControl.setValue(this.itemsToSelect.filter(item => newValuesUnordered.findIndex(_ => this.compareWith(item, _)) >= 0), {emitEvent: false, onlySelf: true});
    }
    this.emitItems();
    this.setSelectAll();
  }

  public emitItems() {
    if (!this.multiple) {
      this.valueChanges.next(this.itemFormControlValue as TData);
      return;
    }
    const values = (this.itemFormControlValue as TData[]).filter(_ => this.isNotSelectAllValue(_));
    this.valueChanges.next(values);
  }

  private setSelectAll() {
    if (!this.multiple) {
      return;
    }
    const itemFormControlValue = this.itemFormControlValue as TData[];
    const values = itemFormControlValue.filter(_ => this.isNotSelectAllValue(_));
    const hasSelectAllValue = itemFormControlValue.findIndex(_ => !this.isNotSelectAllValue(_)) >= 0;
    const allSelected = this.filteredItems.every(item => values.findIndex(_ => this.compareWith(item, _)) >= 0);

    if (allSelected && !hasSelectAllValue && this.filteredItems.length > 0) {
      values.unshift(this.selectAllValue);
      this.itemFormControl.setValue(values, {emitEvent: false, onlySelf: true});
    }
    else if (!allSelected && hasSelectAllValue) {
      this.itemFormControl.setValue(values, {emitEvent: false, onlySelf: true});
    }
  }

  private _filterItems(value: string): TData[] {
    const filterValue = value.toLowerCase();
    return this.itemsToSelect.filter(item => this.getOptionName(item).toLowerCase().includes(filterValue));
  }

  private isNotSelectAllValue(item: TData): boolean {
    return !this.compareWith(item, this.selectAllValue);
  }

  get selectedItemsFormatted(): string {
    if (!this.multiple) {
      return this.getOptionNameTrigger(this.itemFormControlValue as TData);
    }
    const selectedItems = (this.itemFormControlValue as TData[]).filter(_ => this.isNotSelectAllValue(_));
    return selectedItems.map(item => this.getOptionNameTrigger(item)).join(', ');
  }

  get itemFormControl(): FormControl {
    return this.ngControl.control as FormControl<TData[] | TData>;
  }

  get itemFormControlValue(): TData[] | TData {
    return this.itemFormControl.value ?? [];
  }

  get panelClasses(): string[] {
    return ['select-panel-no-gap-on-top', 'select-panel-height-large'];
  }
}
